import { Injectable } from '@angular/core';
import {
    DraasApiCluster,
    DraasApiUpdateVSphereProviderDatastores,
    DraasApiCredentialsChangeRequest,
    DraasApiDatastoresChangeRequest,
    DraasApiVSphereProvider,
    DraasApiAWSProvider,
    DraasApiVmWareDatastore,
    DraasApiNode,
    DraasApiFlashArrayCredentialsChangeRequest,
} from '@pure/paas-api-gateway-client-ts';
import { map, Observable, of, tap } from 'rxjs';
import { DisasterRecoveryCluster } from '../models/disaster-recovery-cluster';
import { DisasterRecoveryCredentialsChangeRequest } from '../models/disaster-recovery-credentials-change-request';
import { DisasterRecoveryFlashArrayCredentialsChangeRequest } from '../models/disaster-recovery-flasharray-credentials-change-request';
import { DraasApiConfig } from './disaster-recovery-constants';
import { IRestResponse } from '../interfaces/collection';
import { DisasterRecoveryDatastoresChangeRequest } from '../models/disaster-recovery-datastores-change-request';
import {
    DisasterRecoveryAwsProvider,
    DisasterRecoveryProvider,
    DisasterRecoveryVSphereProvider,
} from '../models/disaster-recovery-provider';
import { DisasterRecoveryVmWareDatastore } from '../models/disaster-recovery-vmware-datastore';
import { DisasterRecoveryNode } from '../models/disaster-recovery-node';
import { DisasterRecoveryThrottlingHttpClient } from './disaster-recovery-throttling-http-client.service';

@Injectable({ providedIn: 'root' })
export class DisasterRecoveryClusterConfigurationService {
    protected cachedCluster: DisasterRecoveryCluster | null = null;

    constructor(protected http: DisasterRecoveryThrottlingHttpClient) {}

    private getEndpoint(): string {
        return `${DraasApiConfig.getUrlPrefix()}/api/1.0/clusters`;
    }

    getClusterFromCacheOrFirst(force = false): Observable<DisasterRecoveryCluster | null> {
        if (!force && this.cachedCluster) {
            return of(this.cachedCluster);
        }

        const url = this.getEndpoint();

        return this.http.get<DraasApiCluster[]>(url).pipe(
            map(response => response.map(cluster => new DisasterRecoveryCluster(cluster))),
            map(clusters => (clusters.length > 0 ? clusters[0] : null)),
            tap(cluster => (this.cachedCluster = cluster)),
        );
    }

    getCluster(clusterId: string, force = false): Observable<DisasterRecoveryCluster | null> {
        if (!force && this.cachedCluster?.id === clusterId) {
            return of(this.cachedCluster);
        }
        const url = `${this.getEndpoint()}`;

        // TODO: XAAS-15705: Use new api to find one specific cluster once available
        return this.http.get<DraasApiCluster[]>(url).pipe(
            map(response => response.map(cluster => new DisasterRecoveryCluster(cluster))),
            map(clusters => clusters.find(cluster => cluster.id === clusterId) || null),
            tap(cluster => (this.cachedCluster = cluster)),
        );
    }

    getClusters(): Observable<DisasterRecoveryCluster[]> {
        const url = this.getEndpoint();

        return this.http
            .get<DraasApiCluster[]>(url)
            .pipe(map(response => response.map(cluster => new DisasterRecoveryCluster(cluster))));
    }

    getVmWareDatastores(clusterId: string, providerId: string): Observable<DisasterRecoveryVmWareDatastore[]> {
        const queryParams = ['page_number=0', 'page_size=1000'];

        const url = `${DraasApiConfig.getUrlPrefix()}/api/2.0/clusters/${clusterId}/providers/vmware/${providerId}/datastores?${queryParams.join('&')}`;

        return this.http
            .get<IRestResponse<DraasApiVmWareDatastore>>(url)
            .pipe(map(response => response.items.map(it => new DisasterRecoveryVmWareDatastore(it))));
    }

    updateVSphereDatastores(
        clusterId: string,
        providerId: string,
        body: DraasApiUpdateVSphereProviderDatastores,
    ): Observable<DisasterRecoveryDatastoresChangeRequest> {
        const url = `${this.getEndpoint()}/${clusterId}/configurations/vsphere/${providerId}/datastores`;
        return this.http
            .put<DraasApiDatastoresChangeRequest>(url, body)
            .pipe(map(response => new DisasterRecoveryDatastoresChangeRequest(response)));
    }

    getCredentialsChangeRequest(
        clusterId: string,
        providerId: string,
        requestId: string,
    ): Observable<DisasterRecoveryCredentialsChangeRequest> {
        const url = `${this.getEndpoint()}/${clusterId}/providers/${providerId}/credentials-change-requests/${requestId}`;
        return this.http
            .get<DraasApiCredentialsChangeRequest>(url)
            .pipe(map(response => new DisasterRecoveryCredentialsChangeRequest(response)));
    }

    getLatestCredentialsChangeRequest(
        clusterId: string,
        providerId: string,
    ): Observable<DisasterRecoveryCredentialsChangeRequest> {
        const url = `${this.getEndpoint()}/${clusterId}/providers/${providerId}/credentials-change-requests/latest`;
        return this.http
            .get<DraasApiCredentialsChangeRequest>(url)
            .pipe(map(response => new DisasterRecoveryCredentialsChangeRequest(response)));
    }

    getLatestFlashArrayCredentialsChangeRequest(
        clusterId: string,
        providerId: string,
    ): Observable<DisasterRecoveryFlashArrayCredentialsChangeRequest> {
        const url = `${this.getEndpoint()}/${clusterId}/providers/${providerId}/fa-credentials-change-requests/latest`;
        return this.http
            .get<DraasApiFlashArrayCredentialsChangeRequest>(url)
            .pipe(map(response => new DisasterRecoveryFlashArrayCredentialsChangeRequest(response)));
    }

    getDatastoresChangeRequest(
        clusterId: string,
        providerId: string,
        requestId: string,
    ): Observable<DisasterRecoveryDatastoresChangeRequest> {
        const url = `${this.getEndpoint()}/${clusterId}/providers/${providerId}/datastores-change-requests/${requestId}`;
        return this.http
            .get<DraasApiDatastoresChangeRequest>(url)
            .pipe(map(response => new DisasterRecoveryDatastoresChangeRequest(response)));
    }

    getProviders(clusterId: string): Observable<DisasterRecoveryProvider[]> {
        const url = `${this.getEndpoint()}/${clusterId}/providers`;
        return this.http.get<(DraasApiVSphereProvider | DraasApiAWSProvider)[]>(url).pipe(
            map(response =>
                response.map(providerJson => {
                    if (providerJson.provider_type === 'VSPHERE') {
                        return new DisasterRecoveryVSphereProvider(providerJson as DraasApiVSphereProvider);
                    } else {
                        return new DisasterRecoveryAwsProvider(providerJson as DraasApiAWSProvider);
                    }
                }),
            ),
        );
    }

    getProvider(clusterId: string, providerId: string): Observable<DisasterRecoveryProvider> {
        const url = `${this.getEndpoint()}/${clusterId}/providers/${providerId}`;
        return this.http.get<DraasApiVSphereProvider | DraasApiAWSProvider>(url).pipe(
            map(providerJson => {
                if (providerJson.provider_type === 'VSPHERE') {
                    return new DisasterRecoveryVSphereProvider(providerJson as DraasApiVSphereProvider);
                } else {
                    return new DisasterRecoveryAwsProvider(providerJson as DraasApiAWSProvider);
                }
            }),
        );
    }

    getNodes(clusterId: string, providerId: string): Observable<DisasterRecoveryNode[]> {
        const url = `${this.getEndpoint()}/${clusterId}/providers/${providerId}/nodes`;
        return this.http
            .get<DraasApiNode[]>(url)
            .pipe(map(response => response.map(node => new DisasterRecoveryNode(node))));
    }

    renameProvider(
        clusterId: string,
        providerId: string,
        newName: string,
    ): Observable<DisasterRecoveryVSphereProvider | DisasterRecoveryAwsProvider> {
        const url = `${this.getEndpoint()}/${clusterId}/providers/${providerId}/name`;
        return this.http.put<DraasApiVSphereProvider | DraasApiAWSProvider>(url, { name: newName }).pipe(
            map(providerJson => {
                if (providerJson.provider_type === 'VSPHERE') {
                    return new DisasterRecoveryVSphereProvider(providerJson as DraasApiVSphereProvider);
                } else {
                    return new DisasterRecoveryAwsProvider(providerJson as DraasApiAWSProvider);
                }
            }),
        );
    }
}
